import { CheckCircleIcon, ArrowRightIcon, PhoneIcon, ArrowLongRightIcon } from '@heroicons/react/24/solid';
import { useContent } from '../context/ContentContext';

export default function Financing() {
  const { content, settings } = useContent();
  const d = content.financingPlans;
  const phoneDigits = settings.phone.replace(/[^0-9]/g, '');

  return (
    <section id="financing" className="relative overflow-hidden" style={{ backgroundColor: '#111b21' }}>
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-96 h-96 bg-brand-orange/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-brand-orange/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-site mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-28">
        <div className="text-center max-w-2xl mx-auto mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 border border-brand-orange/30 rounded-lg px-4 py-2 mb-6">
            <span className="w-2 h-2 bg-brand-orange rounded-full" />
            <span className="text-brand-orange font-semibold text-sm uppercase tracking-wider">
              {d.sectionLabel}
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-white mb-4 sm:mb-6">
            {d.heading}
          </h2>

          <p className="text-base sm:text-lg text-white/50 leading-relaxed">
            {d.description}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5 sm:gap-6 mb-12 sm:mb-16">
          {d.specials.map((tier, index) => (
            <div
              key={index}
              className={`relative rounded-2xl overflow-hidden border transition-all duration-300 flex flex-col ${
                tier.highlighted
                  ? 'border-brand-orange/30 bg-white/[0.06]'
                  : 'border-white/10 bg-white/[0.03]'
              }`}
            >
              {tier.highlighted && (
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-orange to-transparent" />
              )}

              <div className="p-6 sm:p-7 flex flex-col flex-1">
                <div className="mb-5">
                  <span className={`inline-block text-[10px] font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-4 ${
                    tier.highlighted
                      ? 'bg-brand-orange/20 text-brand-orange'
                      : 'bg-white/10 text-white/50'
                  }`}>
                    {tier.label}
                  </span>

                  <div className="flex items-baseline gap-1 mb-3">
                    {!tier.isRate && <span className="text-sm text-white/40">from</span>}
                    <span className="text-3xl font-bold text-white">{tier.monthly}</span>
                    {!tier.isRate && <span className="text-white/40 text-sm">/mo</span>}
                  </div>

                  <h3 className="text-lg font-serif font-bold text-white mb-1.5">
                    {tier.name}
                  </h3>
                  <p className="text-white/40 text-sm leading-relaxed">{tier.description}</p>
                </div>

                <div className="space-y-2.5 mb-6">
                  {tier.features.map((feature, fIndex) => (
                    <div key={fIndex} className="flex items-center gap-2.5">
                      <CheckCircleIcon className="w-4 h-4 text-brand-orange flex-shrink-0" />
                      <span className="text-white/70 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-auto">
                  <a
                    href={tier.applyUrl}
                    target={tier.applyUrl.startsWith('http') ? '_blank' : undefined}
                    rel={tier.applyUrl.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className={`group inline-flex items-center justify-center gap-2.5 w-full px-6 py-3.5 rounded-lg font-semibold text-sm transition-all duration-300 ${
                      tier.highlighted
                        ? 'bg-brand-orange text-white hover:bg-white hover:text-brand-navy'
                        : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                  >
                    <span>Apply Now</span>
                    <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5 sm:gap-6">
            <div>
              <h3 className="text-lg sm:text-xl font-serif font-bold text-white mb-2">
                {d.ctaHeading}
              </h3>
              <p className="text-white/40 leading-relaxed text-sm sm:text-base">
                {d.ctaDescription}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
              <a
                href="https://apply.optimusfinancing.com/EGIA-488076"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2.5 bg-brand-orange text-white px-6 py-3.5 rounded-lg font-semibold text-sm hover:bg-white hover:text-brand-navy transition-all duration-300"
              >
                {d.ctaButtonLabel}
                <ArrowLongRightIcon className="w-5 h-5" />
              </a>
              <a
                href={`tel:${phoneDigits}`}
                className="inline-flex items-center justify-center gap-3 border border-white/20 text-white px-6 py-3.5 rounded-lg font-semibold text-sm hover:border-brand-orange hover:text-brand-orange transition-all duration-300"
              >
                <PhoneIcon className="w-4 h-4" />
                <span>{settings.phone}</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
